import { Typography, Stack, IconButton, Divider } from '@mui/material'
import { motion } from 'framer-motion'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

import useRecognition from '@/utils/useRecognition'
import { type CardActivity } from '@/models/card-activity/card-activity.model'
import useUtils from '@/hooks/useUtilities'

function VerifyPhrase(props: {
  onClose: () => void
  contextData: CardActivity
  recording: boolean
  onComplete: (data: Partial<CardActivity>) => void
}) {
  const { onClose, contextData, onComplete } = props
  const { showSnackbar } = useUtils()

  const { transcript, listening, resetTranscript, startListening, stopListening, isMicrophoneAvailable } =
    useRecognition({
      target_text: contextData?.target_text || '',
      language: contextData?.language || 'en',
      difficulty: contextData?.difficulty || 'normal',
      delay: 250,
      onMarkIncorrect: () => null,
      onMarkCorrect: () => {
        onComplete({ verificationStatus: 'VERIFIED', checked: true })
        handleStartStopListening()
        onClose()
        showSnackbar('Term verified successfully', 'success')
      },
    })

  const handleStartStopListening = () => {
    if (!isMicrophoneAvailable) {
      alert('Microphone is not available. You must enable it in your browser settings to record.')

      return
    }
    if (listening) {
      stopListening()
    } else {
      startListening()
    }
    resetTranscript()
  }

  return (
    <Stack
      onClick={e => {
        e.stopPropagation()
      }}
    >
      <Stack alignItems="flex-start" gap="16px" padding="24px 24px 20px 24px" width="100%">
        <Stack direction="column" gap="4px" width="100%">
          <Stack direction="row" justifyContent="space-between">
            <Stack alignItems="center" direction="row" gap="4px">
              <Typography fontSize={18} fontWeight={600} lineHeight="28px">
                Verify Term
              </Typography>
            </Stack>
            <Stack direction="row" gap="4px">
              <IconButton size="small" onClick={onClose}>
                <CloseRoundedIcon sx={{ color: '#98A2B3' }} />
              </IconButton>
            </Stack>
          </Stack>
          <Stack>
            <Typography color="#475467" lineHeight="20px">
              We don’t have recognition data for this phrase yet. Record it now to improve accuracy.
            </Typography>
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <Stack alignItems="center" gap="16px" justifyContent="center" minHeight={220} p="20px">
        <Typography fontSize={22} fontWeight={500} lineHeight="32px" textAlign="center">
          {contextData?.target_text}
        </Typography>
        <RecordingButton handleRecord={handleStartStopListening} recording={listening} />
        <Typography minHeight={25}>{transcript}</Typography>
      </Stack>
    </Stack>
  )
}

export default VerifyPhrase

interface RecordingButtonProps {
  recording: boolean
  handleRecord: () => void
}

interface RecordingButtonProps {
  recording: boolean
  handleRecord: () => void
}

function RecordingButton({ recording, handleRecord }: RecordingButtonProps) {
  return (
    <div className="relative flex items-center justify-center">
      {recording ? (
        <motion.div
          animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
          className="absolute w-20 h-20 rounded-full bg-red-500 opacity-50"
          transition={{ repeat: Infinity, duration: 1.5 }}
        />
      ) : null}
      <button
        className="relative z-10 flex items-center justify-center w-16 h-16 bg-orange-600 rounded-full shadow-lg focus:outline-none"
        onClick={handleRecord}
      >
        {!recording && <ButtonIcon />}
      </button>
    </div>
  )
}

function ButtonIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={35} viewBox="0 0 31 35" width={31} xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M15.52 34.414C7.353 34.414.708 27.77.708 19.587S7.352 4.773 15.521 4.773a16.5 16.5 0 0 1 3.262.324l-2.21-2.535a1.163 1.163 0 0 1 .12-1.658 1.163 1.163 0 0 1 1.659.122l4.542 5.216c.216.256.324.58.283.903-.04.324-.215.62-.471.823l-5.298 3.868a1.167 1.167 0 0 1-1.644-.256 1.18 1.18 0 0 1 .256-1.644l3.167-2.319a14.2 14.2 0 0 0-3.666-.472C8.66 7.132 3.066 12.726 3.066 19.587S8.66 32.056 15.52 32.056 27.976 26.462 27.976 19.6c0-2.467-.728-4.867-2.103-6.929a1.17 1.17 0 0 1 .324-1.631 1.17 1.17 0 0 1 1.63.324 14.8 14.8 0 0 1 2.494 8.236c0 8.168-6.645 14.813-14.814 14.813z"
        fill="#fff"
      />
      <path
        d="M8.32 23.054a.91.91 0 0 1-.903-.903v-5.15c0-.484.404-.902.903-.902s.903.404.903.903v5.149a.903.903 0 0 1-.903.903m3.597 1.711a.91.91 0 0 1-.903-.903v-8.558c0-.486.404-.903.903-.903.498 0 .902.404.902.903v8.558a.903.903 0 0 1-.902.903m3.597 1.725a.91.91 0 0 1-.902-.903V13.592c0-.485.404-.903.902-.903.499 0 .903.404.903.903v11.995a.91.91 0 0 1-.903.903m3.598-1.725a.91.91 0 0 1-.903-.903v-8.558c0-.486.404-.903.903-.903.498 0 .902.404.902.903v8.558a.903.903 0 0 1-.902.903m3.598-1.711a.91.91 0 0 1-.904-.903v-5.15c0-.484.405-.902.903-.902.5 0 .904.404.904.903v5.149a.903.903 0 0 1-.904.903"
        fill="#fff"
      />
    </svg>
  )
}
